import React from "react";
import { motion } from "framer-motion";

const symbols = [
  "/symbols/pattern1.svg",
  "/symbols/pattern3.svg",
  "/symbols/pattern4.svg",
  "/symbols/pattern5.svg",
  "/symbols/pattern6.svg",
  "/symbols/pattern7.svg",
  "/symbols/pattern1.svg",
  "/symbols/pattern3.svg",
  "/symbols/pattern4.svg",
  "/symbols/pattern5.svg",
];

const rand = (min, max) => Math.random() * (max - min) + min;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function genSpreadConfigs(count, { w, h, isSmall }) {
  const SYMBOL = 48;

  // bigger safe margin on small screens
  const SAFE_EDGE = isSmall ? 34 : 14;

  // smaller drift on small screens (helps edges + reduces overlap)
  const AMP_X_RANGE = isSmall ? [8, 18] : [10, 34];
  const AMP_Y_RANGE = isSmall ? [6, 16] : [8, 28];

  // min spacing between symbols in px
  const MIN_DIST = isSmall ? 90 : 120;

  const points = []; // store [x,y] in px
  const configs = [];

  const tooClose = (x, y) => {
    for (const [px, py] of points) {
      if (Math.hypot(x - px, y - py) < MIN_DIST) return true;
    }
    return false;
  };

  // each symbol gets its own amp; we place it using bounds that include THAT amp
  for (let i = 0; i < count; i++) {
    const ampX = rand(...AMP_X_RANGE);
    const ampY = rand(...AMP_Y_RANGE);

    const minX = SAFE_EDGE + ampX;
    const maxX = Math.max(minX, w - SYMBOL - SAFE_EDGE - ampX);

    const minY = SAFE_EDGE + ampY;
    const maxY = Math.max(minY, h - SYMBOL - SAFE_EDGE - ampY);

    let x = rand(minX, maxX);
    let y = rand(minY, maxY);

    // try a bunch to avoid clustering
    let tries = 0;
    while (tooClose(x, y) && tries < 2000) {
      x = rand(minX, maxX);
      y = rand(minY, maxY);
      tries++;
    }

    points.push([x, y]);

    configs.push({
      left: `${x}px`,
      top: `${y}px`,
      ampX,
      ampY,
      // all different timings
      dx: rand(8, 18),
      dy: rand(10, 22),
      dr: rand(16, 34),
      // random rotate direction
      rotDir: Math.random() < 0.5 ? -1 : 1,
      // random “phase” so they don’t move together
      delayX: rand(0, 4),
      delayY: rand(0, 4),
      delayR: rand(0, 4),
      easing: pick(["easeInOut", "easeOut"]),
      size: SYMBOL,
    });
  }

  return configs;
}

export default function FloatingSymbols() {
  const configsRef = React.useRef(null);

  // generate ONCE (prevents Safari toolbar resize from teleporting symbols)
  if (!configsRef.current && typeof window !== "undefined") {
    const w = window.innerWidth;
    const h = window.innerHeight; // snapshot once
    const isSmall = w < 640;

    configsRef.current = genSpreadConfigs(symbols.length, { w, h, isSmall });
  }

  const configs = configsRef.current || [];

  return (
    <>
      {symbols.map((src, i) => {
        const c = configs[i];
        if (!c) return null;

        return (
          <motion.img
            key={i}
            src={src}
            alt="Lebanese symbol"
            style={{
              position: "fixed",
              width: `${c.size}px`,
              height: `${c.size}px`,
              opacity: 0.4,
              top: c.top,
              left: c.left,
              pointerEvents: "none",
              userSelect: "none",
              zIndex: 1000,
              filter: "drop-shadow(0 0 3px rgba(184, 140, 90, 0.5))",
            }}
            animate={{
              // mirror = smooth back/forth, no keyframe "reset jump"
              x: [-c.ampX, c.ampX],
              y: [-c.ampY, c.ampY],
              rotate: [0, 360 * c.rotDir],
            }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: "mirror",
                duration: c.dx,
                ease: c.easing,
                delay: c.delayX,
              },
              y: {
                repeat: Infinity,
                repeatType: "mirror",
                duration: c.dy,
                ease: c.easing,
                delay: c.delayY,
              },
              rotate: {
                repeat: Infinity,
                duration: c.dr,
                ease: "linear",
                delay: c.delayR,
              },
            }}
          />
        );
      })}
    </>
  );
}
